/* eslint-disable max-lines, max-len */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FastForwardIcon from '@mui/icons-material/FastForward';
import InfoIcon from '@mui/icons-material/Info';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { lighten, useTheme } from '@mui/material/styles';
import BatchShippingIcon, { batchShippingTooltip } from 'common/components/BatchShippingIcon';
import BusinessAddressIndicator from 'common/components/BusinessAddressIndicator';
import FillLaterPendingIcon from 'common/components/FillLaterPendingIcon';
import ShowOriginalToggle from 'common/components/ShowOriginalToggle';
import ShipByDateCell from 'common/components/ShipByDateCell';
import orderFillType from 'common/constants/orderFillType';
import orderStatus from 'common/constants/orderStatus';
import PrescriptionReviewPayloadProvider from 'common/hooks/usePrescriptionReviewPayloadContext';
import usePrintLabelContext, {
  PrintLabelCheckBox,
  SelectAllCheckBox,
} from 'common/hooks/usePrintLabelContext';
import useRoles from 'common/hooks/useRoles';
import useTabContext from 'common/hooks/useTabContext';
import BatchShipmentDialog from 'common/modules/BatchShipmentDialog';
import FacilityAvatar from 'common/modules/FacilityAvatar';
import VerifiedTag from 'common/modules/VerifiedTag';
import { FillLaterItemStatus } from 'common/constants/fillLater';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import { casingFormat, getTrackingInfo, sortTags, useGetPartnerAccountName } from 'common/utils';
import { format, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { ItemType, SortKey, SortOrderEnum } from 'enums/common.enum';
import lineItemStatusEnum from 'enums/lineItem.enum';
import orderEnum, { orderSource } from 'enums/order.enum';
import { CENTRAL_FILL_REVIEW_REASONS } from 'enums/prescription.enum';
import { VerificationStatus } from 'enums/provider.enum';
import {
  capitalize,
  compact,
  every,
  filter,
  find,
  first,
  flatMap,
  includes,
  isEmpty,
  map,
  orderBy,
  some,
} from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateBatchShipmentFilter } from 'store/thunks/batchShipmentThunk';
import { listPatientOrders, listPatientPrescriptions } from 'store/thunks/patientThunks';
import { applyUrgentTag } from 'store/thunks/workQueueThunks';
import ShipByDateOverrideDialog from '../../../WorkQueue/components/ShipByDateOverrideDialog';
import PatientPrescriptionDialog from '../PrescriptionsTab/PatientPrescriptionDialog';
import CancelOrderButton from './CancelOrderButton';
import EditOrderAddressButton from './EditOrderAddressButton';
import FillOverridesDialog from './FillOverridesDialog';
import ModifyAssociationsButton from './ModifyAssociationButton';
import FillLaterMenuButton from './FillLaterMenuButton';
import { buildMoveableDialogLineItems } from './fillLaterOptions';
import OrderEventHistoryButton from './OrderEventHistoryButton';
import PatientFillProductButton from './PatientFillProductButton';
import PatientNoteOptions from './PatientNoteOptions';
import ReshipOrderButton from './ReshipOrderButton';
import ReverifyOrderButton from './ReVerifyOrderButton';
import ReviewOrderVerificationButton from './ReviewOrderVerificationButton';
import ReviewPrescriptionButton from './ReviewPrescriptionButton';
import SendOrderBackToQueueButton from './SendOrderBackToQueueButton';
import SendToShippingButton from './SendToShippingButton';
import ShipLaterButton from './ShipLaterButton';
import ShipmentHistoryButton from './ShipmentHistoryButton';
import TransferOrderButton from './TransferOrderButton';
import TransferOutDocumentViewer from './TransferOutDocumentViewer';
import VerifyOrderButton from './VerifyOrderButton';
import VoidShipmentButton from './VoidShipmentButton';
import {
  canMoveSelectedItemsToFillLater,
  isMovableLineItem,
  selectSplittableLineItems,
} from './fillLaterEligibility';

// POM-6148: fillDate is a date-only string. parseISO keeps it at local midnight so the
// chip cannot show the previous day the way new Date('yyyy-MM-dd') would.
const formatFillLaterDate = (fillDate) =>
  fillDate ? format(parseISO(fillDate), 'MM/dd/yyyy') : '';

// POM-6221: why the print checkbox is closed. The box was already disabled on a void order, but
// with no reason given the tooltip fell through to PrintLabelCheckBox's last resort, 'Product in
// review' -- which describes neither the order nor the item, and is what QA saw on hover. A
// VOID order is parked until its Fill Later date, not refused, so it is said in its own words.
// The order is read before the line item because the order is what the operator is looking at.
/**
 * A split leaves the line item PROCESSING with its dispensing quantity reduced; moving the item,
 * or the whole order, parks it at VOID with the quantity untouched. So VOID is the signal that
 * nothing was left behind, and a parked Fill Later order tells the two moves apart.
 */
const fillLaterPendingScope = ({ status, isOrderFillLater, processingState }) => {
  if (processingState !== 'FILL_LATER') return 'PARTIAL';

  return status === orderEnum.FILL_LATER && isOrderFillLater ? 'ORDER' : 'ITEM';
};

const labelDisabledReason = ({ status, itemStatus, processingState }) => {
  if (status === orderEnum.FILL_LATER) return 'Order is parked for Fill Later';
  if (status === orderEnum.CANCELED) return 'Order is cancelled';
  if (processingState === 'FILL_LATER') return 'Item is parked for Fill Later';
  if (itemStatus === lineItemStatusEnum.CANCELED || processingState === 'CANCELED')
    return 'Item is cancelled';
  return undefined;
};

const PatientOrdersAccordion = ({ order, mpi }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const formatTagText = (tag) => {
    const tagName = tag?.tagName || '';
    return tagName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const orderLineItemIds = map(
    filter(
      order.lineItems,
      ({ lineItemStatus }) => lineItemStatus !== lineItemStatusEnum.RE_PROCESS
    ),
    'lineItemId'
  );

  const allActiveLineItemIds = map(
    filter(
      order?.lineItems,
      ({ taggedForReview, lineItemStatus, isLabelCreated, processingState }) =>
        order.status !== orderEnum.CANCELED &&
        order.status !== orderEnum.FILL_LATER &&
        lineItemStatus !== lineItemStatusEnum.RE_PROCESS &&
        lineItemStatus !== lineItemStatusEnum.CANCELED &&
        !includes(['CANCELED', 'FILL_LATER'], processingState) &&
        !taggedForReview &&
        !!isLabelCreated
    ),
    'lineItemId'
  );

  const isLineItemsInReprocessingState = some(
    order.lineItems,
    ({ lineItemStatus }) => lineItemStatus === lineItemStatusEnum.RE_PROCESS
  );
  const isLineItemsHasLabelCreated = some(
    order.lineItems,
    ({ isLabelCreated }) => isLabelCreated === true
  );

  const [activeNoteItem, setActiveNoteItem] = useState(null);
  const [fillOverridesForItem, setFillOverridesForItem] = useState([]);
  const [showOriginalLineItemIds, setShowOriginalLineItemIds] = useState([]);
  const [transferDocumentViewerItem, setTransferDocumentViewerItem] = useState(null);
  const [viewPrescriptionId, setViewPrescriptionId] = useState(null);
  const [shipByDateLineItemId, setShipByDateLineItemId] = useState(null);
  const [urgentLoadingId, setUrgentLoadingId] = useState(null);
  const [isEligibleForReshipment, setIsEligibleForReshipment] = useState(false);
  const [openBatchShipmentDialog, setOpenBatchShipmentDialog] = useState(false);
  const [currentBatchShipmentId, setCurrentBatchShipmentId] = useState(null);
  const hasAccess = useRoles();
  // POM-6320: the movability rule is shared with Order View, which offers the same three scopes.
  // A fulfilment gate stated in two places is one that can be changed in one.
  const movableLineItems = filter(order.lineItems, isMovableLineItem);
  const canMoveToFillLater = canMoveSelectedItemsToFillLater({
    lineItems: order.lineItems,
    hasSplitAccess: hasAccess.splitLineItemQuantity,
  });

  const displaySendToShipmentButton = includes(
    [orderEnum.ORDER_VERIFIED, orderEnum.HOLD],
    order.status
  );

  const partnerAccountHandler = useGetPartnerAccountName();

  const getPartnerAccountName = (partnerAccountId) => {
    return partnerAccountId && partnerAccountHandler[partnerAccountId]
      ? capitalize(partnerAccountHandler[partnerAccountId].partnerAccountName)
      : 'N/A';
  };

  const displayOrderCost = includes(
    [
      orderEnum.ORDER_VERIFIED,
      orderEnum.ADDED_TO_SHIPMENT,
      orderEnum.ORDER_VERIFICATION,
      orderEnum.ORDER_RE_VERIFICATION,
    ],
    order.status
  );
  const patientPrescriptions = useSelector(({ patient }) => patient[mpi]?.prescriptions ?? []);
  const prescriptionsLoaded = useSelector(({ patient }) => patient[mpi]?.prescriptions != null);
  const isValidOrder = order?.orderType === 'RX_ENTRY' || order?.orderType === 'OTC';
  const isManualOrder = orderSource[order.source].sourceKey === 'Manual';
  const isRefillOrder = order.fillType === 'REFILL';

  const showNeedsReviewButtonOnProductFulfillment =
    isValidOrder &&
    hasAccess.markOrderForExternalProcessing &&
    !order.isProcessedExternal &&
    !every(order.lineItems, 'taggedForReview') &&
    includes([orderEnum.OPEN, orderEnum.ORDER_VERIFICATION], order.status);

  const showNeedsReviewButton =
    includes(['OPEN', 'ORDER_VERIFICATION', 'ORDER_RE_VERIFICATION'], order.status) &&
    some(order.lineItems, { taggedForReview: false }) &&
    !order.isProcessedExternal;

  const isLineItemsInBulkShipmentQueue = some(order.lineItems, { inBatchShipmentQueue: true });
  // POM-6261: how many items on this order still owe a Fill Later, so the cancel dialog can warn
  // that cancelling the order cancels those too and does not give their quantity back.
  const fillLaterItemCount = filter(order.lineItems, { isMarkedForFillLater: true }).length;
  const batchShipmentId = order.lineItems?.find((li) => li.inBatchShipmentQueue)?.batchShipmentId;
  const isInShipment = some(
    order.lineItems,
    ({ inBatchShipmentQueue, processingState }) =>
      inBatchShipmentQueue && processingState === 'IN_SHIPMENT'
  );

  const showVerifyOrderBtn =
    includes([orderEnum.ORDER_VERIFICATION, orderEnum.ORDER_RE_VERIFICATION], order.status) &&
    hasAccess.verifyOrder;

  const showTransferOrderButton = includes(
    ['OPEN', 'ORDER_VERIFICATION', 'ORDER_RE_VERIFICATION'],
    order.status
  );

  const orderHistory = useSelector(({ patient }) => patient[mpi]?.orderHistory ?? []);
  const inOrderHistoryTab = useTabContext()?.tab === 'OrderHistoryTab';

  useEffect(() => {
    if (!inOrderHistoryTab || order?.status !== orderEnum.SHIPPED) {
      return;
    }

    const isReshipmentOrder = !!order?.reshipmentOf;
    const currentOrderRxNumbers = map(order.lineItems, 'rxNumber');

    const otherOrderHistoryOrders = filter(
      orderHistory,
      ({ orderId }) => orderId !== order?.orderId
    );

    // If this is the parent/original order, and there is any active (non-cancelled)
    // reshipment order for the same RX numbers, then block reshipment on the parent.
    const hasActiveReshipForSameRx =
      !isReshipmentOrder &&
      some(otherOrderHistoryOrders, (otherOrder) => {
        if (!otherOrder?.reshipmentOf) return false;

        return some(
          otherOrder?.lineItems ?? [],
          ({ rxNumber, processingState }) =>
            rxNumber && includes(currentOrderRxNumbers, rxNumber) && processingState !== 'CANCELED'
        );
      });

    if (hasActiveReshipForSameRx) {
      return;
    }

    const isCurrentOrderItemInShippedState = !isEmpty(
      filter(order.lineItems, (item) => item.processingState === 'SHIPPED')
    );

    if (isCurrentOrderItemInShippedState) {
      setIsEligibleForReshipment(true);
      return;
    }

    // Only consider child reship orders (reships created from this order), not all other orders.
    const childReshipOrders = filter(
      orderHistory,
      ({ orderId, reshipmentOf }) => orderId !== order?.orderId && reshipmentOf === order?.orderId
    );

    const allChildReshipLineItems = flatMap(
      childReshipOrders,
      (childOrder) => childOrder?.lineItems ?? []
    );
    const isAnyChildReshipItemInCancelledState = some(
      allChildReshipLineItems,
      ({ rxNumber, processingState }) =>
        includes(currentOrderRxNumbers, rxNumber) && processingState === 'CANCELED'
    );

    if (isAnyChildReshipItemInCancelledState) {
      setIsEligibleForReshipment(true);
    }
  }, [order, orderHistory, inOrderHistoryTab]);

  const userFacilityIds = useSelector(({ auth }) =>
    map(auth?.fulfillingPharmacies, 'fulfillingPharmacyId')
  );

  const userOrderAccess = includes(userFacilityIds, order?.fulfillingPharmacyId);

  const showReshipmentButton = hasAccess.reshipOrder && isEligibleForReshipment && userOrderAccess;

  const { labelItemIds, setLabelItemIds } = usePrintLabelContext();
  const enablePrintLabelButton = () => {
    setLabelItemIds([...orderLineItemIds]);
  };

  // POM-6199: Fill Later reuses the line item checkboxes rather than adding a second column
  // of its own — whatever is ticked for printing is what the Fill Later button acts on. Only
  // the ticked items that can actually be split reach the dialog, so it shows the selection
  // rather than the whole order; the rest are counted so the button's tooltip can name them
  // instead of dropping them silently. Shared with Order View (POM-6320).
  const { selected: splitSelectedLineItems, skippedCount: splitSkippedCount } =
    selectSplittableLineItems({
      lineItems: order.lineItems,
      selectedLineItemIds: labelItemIds,
      isProcessedExternal: order.isProcessedExternal,
      currentOrderStatus: order.status,
      hasSplitAccess: hasAccess.splitLineItemQuantity,
    });

  // POM-6219: a VOID order is parked until its Fill Later date arrives. It stays listed in Order
  // History so it remains in the patient's record, but the fulfilment actions must not act on it;
  // the status lists below were written before VOID existed and none of them catch it.
  // Modify Associations is deliberately NOT gated: re-pointing an order at the right patient or
  // practice is record correction, not fulfilment, and is still needed while the order is parked.
  const isParkedOrder = order.status === orderEnum.FILL_LATER;

  const isCompoundingReviewOrder = some(order.lineItems, ({ reviewReason }) =>
    includes(CENTRAL_FILL_REVIEW_REASONS, reviewReason)
  );

  const displayEditOrderAddressButton =
    isValidOrder &&
    hasAccess.updateOrderAddress &&
    order.status !== orderEnum.SHIPPED &&
    order.status !== orderEnum.CANCELED &&
    order.status !== orderEnum.TRANSFERRED &&
    order.status !== orderEnum.FILL_LATER &&
    !order.isProcessedExternal &&
    !isCompoundingReviewOrder;

  const shouldShowTransferOrderButton =
    showTransferOrderButton &&
    hasAccess.transferOrder &&
    !order.isProcessedExternal &&
    !isCompoundingReviewOrder;

  const displayReverifyOrderBtn =
    isValidOrder && !order.isProcessedExternal && order.status === orderEnum.ADDED_TO_SHIPMENT;

  const isSendBackInvalidOrderStatus = includes(
    [
      orderEnum.EXPIRED,
      orderEnum.TRANSFERRED,
      orderEnum.SHIPPED,
      orderEnum.CANCELED,
      orderEnum.FILL_LATER,
    ],
    order.status
  );
  const isBatchedWithShipmentCreated = some(
    order.lineItems,
    ({ inBatchShipmentQueue, processingState }) =>
      inBatchShipmentQueue && processingState === 'SHIPPED'
  );
  const isOrderInReview = some(
    order.lineItems,
    ({ taggedForReview, processingState }) =>
      taggedForReview && !includes(['CANCELED', 'SPLIT'], processingState)
  );
  const isSendBackToQueueDisabled =
    isSendBackInvalidOrderStatus || isBatchedWithShipmentCreated || isOrderInReview;

  const currentTenantId = useSelector(({ auth }) => auth.currentTenantId);

  const fulfillingPharmacies = useSelector(({ pharmacy }) => pharmacy[currentTenantId]);
  const fillingFacility = find(
    fulfillingPharmacies,
    (fulfillingPharmacy) => fulfillingPharmacy?.fulfillingPharmacyId === order.fulfillingPharmacyId
  );

  const rxPrefix = fillingFacility?.config?.rxPrefix;
  const facilityName = fillingFacility?.name;
  const facilityState = fillingFacility?.state;

  const headCells = [
    {
      id: 'shipByDate',
      label: 'Ship By',
    },
    {
      id: 'needsByDate',
      label: 'Needs By',
    },
    {
      id: 'itemName',
      label: 'Item',
    },
    {
      id: 'prescriberName',
      label: 'Prescriber',
    },
    {
      id: 'requestedDate',
      label: 'Requested At',
    },
    {
      id: 'type',
      label: 'Type',
    },
    {
      id: 'rxNumber',
      label: 'RX Number',
    },
    {
      id: 'px',
      label: 'Px',
    },
    {
      id: 'strength',
      label: 'Strength',
      align: 'center',
    },
    {
      id: 'form',
      label: 'Form',
    },
    {
      id: 'prescribedQuantity',
      label: 'Prescribed Quantity',
      align: 'center',
    },
    {
      id: 'quantity',
      label: inOrderHistoryTab ? 'Dispensed Quantity' : 'Quantity to Dispense',
      align: 'center',
    },
    {
      id: 'partnerAccount',
      label: 'Partner Account',
      align: 'center',
    },
    {
      id: 'docutrackId',
      label: 'Doc #',
      align: 'center',
    },
  ];

  const border = (item) =>
    !inOrderHistoryTab || (item.processingState === 'RESHIPMENT' && !isEmpty(item.reshipmentReason))
      ? { borderBottom: 'none' }
      : {};

  const handleCopyOrderId = (event) => {
    event.stopPropagation();
    navigator.clipboard.writeText(order.orderId);
  };

  const handleCopyLineItemId = (event, lineItemId) => {
    event.stopPropagation();
    navigator.clipboard.writeText(lineItemId);
  };

  const navigateToBatchShipmentQueue = (orderId) => {
    dispatch(
      updateBatchShipmentFilter({
        filter: { orderId },
      })
    );
    navigate('/batchShipments');
  };

  const handleOpenBatchShipmentDialog = (bShipmentId) => {
    setCurrentBatchShipmentId(bShipmentId);
    setOpenBatchShipmentDialog(true);
  };

  const handleCloseBatchShipmentDialog = () => {
    setCurrentBatchShipmentId(null);
    setOpenBatchShipmentDialog(false);
  };

  return (
    <Grid container direction='column' sx={{ mb: 1 }}>
      <BatchShipmentDialog
        batchShipmentId={currentBatchShipmentId}
        onClose={handleCloseBatchShipmentDialog}
        open={openBatchShipmentDialog}
      />
      <Accordion defaultExpanded disableGutters>
        <AccordionSummary
          sx={{ bgcolor: lighten(theme.palette.text.primary, 0.9) }}
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${order.orderId}-panel-content`}
          id={`${order.orderId}-panel-header`}
        >
          <Grid container justifyContent='space-between' alignItems='center'>
            <Stack direction='row' alignItems='center' spacing={1}>
              <Typography variant='subtitle2' fontWeight='bold'>
                <Box component='span' display='inline-flex' alignItems='center'>
                  {`Order: ${order.orderId}`}
                  <Tooltip title='Copy Order Id' arrow>
                    <IconButton size='small' onClick={handleCopyOrderId} sx={{ ml: 0.5 }}>
                      <ContentCopyIcon fontSize='inherit' />
                    </IconButton>
                  </Tooltip>
                </Box>
                {`| Source: ${
                  orderSource[order.source]?.sourceKey || 'NA'
                } | Partner: ${order?.partner?.partnerName} | Practice: ${
                  casingFormat(order?.practice?.practiceName) || 'NA'
                }`}
              </Typography>
              <Chip
                sx={{
                  background: theme.palette.secondary.light,
                  color: theme.palette.primary.contrastText,
                  height: 35,
                }}
                label={
                  <Typography variant='body2' fontWeight='bold'>
                    {orderStatus[order.status]}
                  </Typography>
                }
              />

              <Chip
                sx={{
                  background:
                    order.fillType === 'REFILL'
                      ? theme.palette.info.dark
                      : theme.palette.success.dark,
                  color: theme.palette.primary.contrastText,
                  height: 35,
                  width: order.fillType === 'REFILL' ? '55px' : '65px',
                }}
                label={
                  <Typography sx={{ fontSize: '1.1em' }}>
                    {orderFillType[order.fillType]}
                  </Typography>
                }
              />

              {order.reshipmentOf && (
                <Chip
                  sx={{
                    height: 35,
                    color: theme.palette.primary.contrastText,
                    background: theme.palette.info.dark,
                  }}
                  label={
                    <Typography sx={{ fontSize: '1.1em' }}>
                      Reshipment of {order.reshipmentOf}
                    </Typography>
                  }
                />
              )}

              {/* isFillLater, not fillLaterOf: a whole-order move parks the order in place and
                  leaves parent_order_id null, so the pointer below is absent even though the order
                  is in Fill Later.

                  POM-6320: left off once the status itself reads Fill Later, where the chip beside
                  it says the same word and adds nothing. It still shows on a split child, which is
                  OPEN and being filled now and carries the flag as its only sign of where it came
                  from. */}
              {order.isFillLater && order.status !== orderEnum.FILL_LATER && (
                <Chip
                  sx={{
                    height: 35,
                    color: theme.palette.primary.contrastText,
                    background: theme.palette.info.dark,
                  }}
                  label={<Typography sx={{ fontSize: '1.1em' }}>Fill Later</Typography>}
                />
              )}

              {/* Only a split off a different order has a source to name. */}
              {order.fillLaterOf && order.fillLaterOf !== order.orderId && (
                <Chip
                  sx={{
                    height: 35,
                    color: theme.palette.primary.contrastText,
                    background: theme.palette.info.dark,
                  }}
                  label={
                    <Typography sx={{ fontSize: '1.1em' }}>Split of {order.fillLaterOf}</Typography>
                  }
                />
              )}
            </Stack>

            <Stack direction='row' alignItems='center' spacing={4}>
              {facilityName && (
                <Typography variant='subtitle2'>
                  Filling At:
                  <strong>
                    {facilityName} - {facilityState}
                  </strong>
                </Typography>
              )}

              {displayOrderCost && order.orderCost && (
                <Typography variant='subtitle2' sx={{ mr: 2, fontWeight: 600 }}>
                  Order Total: ${order.orderCost}
                </Typography>
              )}
            </Stack>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid sx={{ my: 1 }} container direction='column'>
            <Grid item>
              <TableContainer>
                <Grid container justifyContent='flex-end' sx={{ pb: 2 }} columnGap={1}>
                  {order.status !== orderEnum.SHIPPED &&
                    order.status !== orderEnum.CANCELED &&
                    order.status !== orderEnum.EXPIRED &&
                    order.status !== orderEnum.TRANSFERRED &&
                    !isParkedOrder &&
                    !isCompoundingReviewOrder && (
                      <Tooltip
                        title={
                          isLineItemsInBulkShipmentQueue &&
                          order.status === orderEnum.ADDED_TO_SHIPMENT
                            ? 'Order is part of a batch shipment and cannot be cancelled'
                            : undefined
                        }
                      >
                        <span>
                          <CancelOrderButton
                            disabled={
                              isLineItemsInReprocessingState ||
                              (isLineItemsInBulkShipmentQueue &&
                                order.status === orderEnum.ADDED_TO_SHIPMENT)
                            }
                            orderId={order.orderId}
                            mpi={mpi}
                            hasShipment={order.status === orderEnum.ADDED_TO_SHIPMENT}
                            fillLaterCount={fillLaterItemCount}
                          />
                        </span>
                      </Tooltip>
                    )}
                  {displayReverifyOrderBtn && (
                    <Tooltip
                      title={
                        isLineItemsInBulkShipmentQueue
                          ? 'Order line items in batch shipment cannot be reverified'
                          : undefined
                      }
                      placement='top'
                      arrow
                    >
                      <span>
                        <ReverifyOrderButton
                          mpi={mpi}
                          orderId={order.orderId}
                          disabled={isLineItemsInBulkShipmentQueue}
                        />
                      </span>
                    </Tooltip>
                  )}
                  {displayEditOrderAddressButton &&
                    order.status !== orderEnum.EXPIRED &&
                    order.status !== orderEnum.TRANSFERRED && (
                      <EditOrderAddressButton
                        mpi={mpi}
                        orderId={order.orderId}
                        orderAddress={order.shippingAddress}
                        deliveryInstructions={order.deliveryInstructions}
                        afterAddressUpdate={enablePrintLabelButton}
                        disabled={isLineItemsInReprocessingState}
                      />
                    )}
                  {showNeedsReviewButton && (
                    <ReviewPrescriptionButton
                      disabled={isLineItemsInReprocessingState}
                      patientId={mpi}
                      orderId={order.orderId}
                      lineItems={filter(
                        order.lineItems,
                        (lineItem) => !includes(['CANCELED', 'SPLIT'], lineItem.processingState)
                      )}
                      patientPrescriptions={patientPrescriptions}
                    />
                  )}
                  {showNeedsReviewButtonOnProductFulfillment && !isCompoundingReviewOrder && (
                    <ReviewOrderVerificationButton
                      mpi={mpi}
                      orderId={order.orderId}
                      isRefillOrder={isRefillOrder}
                      isManualOrder={isManualOrder}
                      disabled={isLineItemsInReprocessingState}
                    />
                  )}
                  {showVerifyOrderBtn && (
                    <VerifyOrderButton
                      mpi={mpi}
                      order={order}
                      isManualOrder={isManualOrder}
                      isRxEntryOrder={isValidOrder}
                      patientPrescriptions={patientPrescriptions}
                    />
                  )}
                  {displaySendToShipmentButton && hasAccess.createOrderShipment && (
                    <SendToShippingButton
                      mpi={mpi}
                      orderId={order.orderId}
                      orderStatus={order.status}
                    />
                  )}
                  {shouldShowTransferOrderButton && (
                    <TransferOrderButton
                      mpi={mpi}
                      orderId={order.orderId}
                      orderFacilityId={order.fulfillingPharmacyId}
                      lineItems={order.lineItems}
                    />
                  )}
                  {hasAccess.updateOrderAssociation &&
                    userOrderAccess &&
                    order.status !== orderEnum.EXPIRED &&
                    order.status !== orderEnum.TRANSFERRED &&
                    !isCompoundingReviewOrder && (
                      <ModifyAssociationsButton mpi={mpi} orderId={order?.orderId} order={order} />
                    )}
                  {hasAccess.ViewOrderEventHistory && (
                    <OrderEventHistoryButton
                      referenceId={order.referenceId}
                      orderId={order.orderId}
                      requestId={order.requestId}
                      mpi={mpi}
                      partnerId={order.partner.partnerUuid}
                    />
                  )}
                  <ShipmentHistoryButton orderId={order.orderId} />
                  {order.status === orderEnum.SHIPPED && showReshipmentButton && (
                    <ReshipOrderButton
                      lineItems={order.lineItems}
                      mpi={mpi}
                      orderId={order.orderId}
                      partnerUuid={order.partner.partnerUuid}
                    />
                  )}
                  {order.status === orderEnum.SHIPPED && (
                    <VoidShipmentButton
                      mpi={mpi}
                      orderId={order.orderId}
                      lineItems={order.lineItems}
                      order={order}
                    />
                  )}
                  {!isCompoundingReviewOrder && (
                    <SendOrderBackToQueueButton
                      mpi={mpi}
                      orderId={order.orderId}
                      disabled={isSendBackToQueueDisabled}
                      isInBatch={isLineItemsInBulkShipmentQueue}
                      batchShipmentId={batchShipmentId}
                      isInShipment={isInShipment}
                    />
                  )}
                  <ShipLaterButton
                    mpi={mpi}
                    orderId={order.orderId}
                    disabled={includes(
                      [
                        orderEnum.ADDED_TO_SHIPMENT,
                        orderEnum.SHIPPED,
                        orderEnum.CANCELED,
                        orderEnum.FILL_LATER,
                      ],
                      order.status
                    )}
                  />
                  {/* POM-6249: one way into Fill Later. The three scopes - whole order,
                      selected items, split by quantity - are named in the menu instead of
                      being three toolbar buttons, two of which read identically. */}
                  <FillLaterMenuButton
                    mpi={mpi}
                    order={order}
                    orderDetails={{
                      status: order.status,
                      source: orderSource[order.source]?.sourceKey || 'NA',
                      fulfillingPharmacy: facilityName,
                      partnerName: order?.partner?.partnerName || 'NA',
                      practiceName: casingFormat(order?.practice?.practiceName) || 'NA',
                    }}
                    movableLineItems={movableLineItems}
                    canMoveToFillLater={canMoveToFillLater}
                    splitSelectedLineItems={splitSelectedLineItems}
                    splitSkippedCount={splitSkippedCount}
                    moveableDialogLineItems={buildMoveableDialogLineItems(order.lineItems)}
                  />
                </Grid>
                <Table size='small'>
                  <TableHead>
                    <TableRow hover>
                      {headCells.map((headCell) => (
                        <TableCell key={headCell.id} align={headCell.align}>
                          {headCell.label}
                        </TableCell>
                      ))}

                      <TableCell align='center'>Shipping Info</TableCell>
                      <TableCell align='center' sx={{ py: 0 }}>
                        {hasAccess.generateBulkLabels &&
                          !order.isProcessedExternal &&
                          !isLineItemsInReprocessingState &&
                          isLineItemsHasLabelCreated && (
                            <SelectAllCheckBox allLineItemIds={allActiveLineItemIds} />
                          )}
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {map(
                      orderBy(order.lineItems, [SortKey.RX_NUMBER], [SortOrderEnum.ASC]),
                      (item) => {
                        const hasOverride = !!(item.overriddenItemName || item.overriddenStrength);
                        const isShowingOriginal = showOriginalLineItemIds.includes(item.lineItemId);
                        let displayItemName = item.itemName;
                        let displayStrength = item.strength;
                        let displayNdc = item.ndc;
                        if (hasOverride && isShowingOriginal) {
                          displayItemName = item.originalItemName;
                          displayNdc = item.originalNdc;
                        } else if (hasOverride) {
                          displayItemName = item.overriddenItemName;
                          displayStrength = item.overriddenStrength;
                        }
                        return (
                          <React.Fragment key={item.lineItemId}>
                            <TableRow key={item.lineItemId}>
                              <TableCell
                                sx={{
                                  paddingY: '12px',
                                  ...border(item),
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <ShipByDateCell
                                    shipByDate={item.shipByDate}
                                    tags={item.tags}
                                    onClick={
                                      !includes(
                                        [
                                          orderEnum.CANCELED,
                                          orderEnum.EXPIRED,
                                          orderEnum.TRANSFERRED,
                                          orderEnum.SHIPPED,
                                        ],
                                        order.status
                                      ) &&
                                      !includes(
                                        ['CANCELED', 'SHIPPED', 'SPLIT', 'RESHIPMENT'],
                                        item.processingState
                                      )
                                        ? () => setShipByDateLineItemId(item.lineItemId)
                                        : undefined
                                    }
                                  />
                                  {!includes(
                                    [
                                      orderEnum.CANCELED,
                                      orderEnum.EXPIRED,
                                      orderEnum.TRANSFERRED,
                                      orderEnum.SHIPPED,
                                    ],
                                    order.status
                                  ) &&
                                    !includes(
                                      ['CANCELED', 'SHIPPED', 'SPLIT', 'RESHIPMENT'],
                                      item.processingState
                                    ) &&
                                    !(
                                      find(item.tags, { tagName: 'URGENT' }) &&
                                      item.shipByDate === format(new Date(), 'yyyy-MM-dd')
                                    ) && (
                                      <Tooltip title='Mark as Urgent Today' arrow>
                                        <span>
                                          <IconButton
                                            size='small'
                                            color='error'
                                            disabled={urgentLoadingId === item.lineItemId}
                                            onClick={async () => {
                                              setUrgentLoadingId(item.lineItemId);
                                              try {
                                                await dispatch(
                                                  applyUrgentTag({
                                                    lineItemId: item.lineItemId,
                                                    shipByDate: format(new Date(), 'yyyy-MM-dd'),
                                                    mpi,
                                                    orderId: order.orderId,
                                                  })
                                                );
                                                enqueueSnackbar('Urgent tag applied', {
                                                  variant: 'success',
                                                });
                                              } catch {
                                                enqueueSnackbar('Failed to apply urgent tag', {
                                                  variant: 'error',
                                                });
                                              } finally {
                                                setUrgentLoadingId(null);
                                              }
                                            }}
                                          >
                                            {urgentLoadingId === item.lineItemId ? (
                                              <CircularProgress size={12} color='error' />
                                            ) : (
                                              <FastForwardIcon fontSize='small' />
                                            )}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    )}
                                </Box>
                              </TableCell>
                              <TableCell
                                sx={{
                                  paddingY: '12px',
                                  color: 'text.disabled',
                                  ...border(item),
                                }}
                              >
                                <Tooltip
                                  title='Legacy field — see Ship By Date'
                                  placement='top'
                                  arrow
                                >
                                  <span>
                                    {item.needsByDate
                                      ? format(
                                          utcToZonedTime(new Date(item.needsByDate), 'utc'),
                                          'MM/dd/yyyy'
                                        )
                                      : '—'}
                                  </span>
                                </Tooltip>
                              </TableCell>
                              <TableCell
                                sx={{
                                  paddingY: '12px',
                                  ...border(item),
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                  }}
                                >
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                    <Box component='span'>{displayItemName}</Box>
                                    {hasOverride && (
                                      <ShowOriginalToggle
                                        isShowingOriginal={isShowingOriginal}
                                        onToggle={() =>
                                          setShowOriginalLineItemIds((prev) =>
                                            prev.includes(item.lineItemId)
                                              ? prev.filter((id) => id !== item.lineItemId)
                                              : [...prev, item.lineItemId]
                                          )
                                        }
                                      />
                                    )}
                                    {item.lineItemId && (
                                      <Tooltip
                                        title={`ItemId: ${item.lineItemId}`}
                                        arrow
                                        placement='top'
                                        componentsProps={{
                                          tooltip: {
                                            sx: {
                                              bgcolor: lighten(theme.palette.text.primary, 0.9),
                                              color: theme.palette.text.primary,
                                            },
                                          },
                                          arrow: {
                                            sx: {
                                              color: lighten(theme.palette.text.primary, 0.9),
                                            },
                                          },
                                        }}
                                      >
                                        <IconButton
                                          size='small'
                                          onClick={(e) => handleCopyLineItemId(e, item.lineItemId)}
                                          sx={{ ml: 0.5 }}
                                        >
                                          <ContentCopyIcon fontSize='inherit' />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'block' }}>
                                    <Typography
                                      variant='body2'
                                      color={displayNdc ? 'text.secondary' : 'error'}
                                      sx={{ fontWeight: 'normal' }}
                                    >
                                      NDC: {displayNdc || 'N/A'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell
                                align={item.itemType === ItemType.OTC ? 'center' : 'left'}
                                sx={{
                                  paddingY: '12px',
                                  ...border(item),
                                }}
                              >
                                {item.itemType === ItemType.OTC ? (
                                  '-'
                                ) : (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    <Typography component='span' variant='body2'>
                                      {casingFormat(item.prescriberName ?? '') || 'N/A'}
                                    </Typography>
                                    {(item.processingState === 'PROCESSING' ||
                                      item.processingState === 'READY_FOR_SHIPMENT') && (
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          flexShrink: 0,
                                          '& svg': {
                                            fontSize: '1.4rem',
                                          },
                                        }}
                                      >
                                        <VerifiedTag
                                          status={
                                            VerificationStatus[item.verificationStatus] ||
                                            VerificationStatus.UNVERIFIED
                                          }
                                        />
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell sx={{ ...border(item) }}>
                                {format(new Date(item.requestedDate), 'MM/dd/yyyy')}
                              </TableCell>
                              <TableCell
                                sx={{
                                  paddingY: '12px',
                                  ...border(item),
                                }}
                              >
                                {item.itemType}
                              </TableCell>
                              <TableCell sx={{ ...border(item) }}>
                                <Grid container alignItems='center'>
                                  <Grid item>
                                    {item.itemType !== ItemType.OTC && item.prescriptionId ? (
                                      <Button
                                        variant='text'
                                        size='small'
                                        onClick={() => setViewPrescriptionId(item.prescriptionId)}
                                        sx={{
                                          p: 0,
                                          minWidth: 0,
                                          textTransform: 'none',
                                          fontSize: 'inherit',
                                          color: 'text.primary',
                                          textDecoration: 'underline',
                                          '&:hover': { textDecoration: 'underline' },
                                        }}
                                      >
                                        {item.rxNumber}
                                      </Button>
                                    ) : (
                                      item.rxNumber
                                    )}
                                  </Grid>
                                  {item.showOldRxNumber && (
                                    <Grid item>
                                      <Tooltip
                                        title={`Rx Number updated. Previous Rx Number: ${item.oldRxNumber}`}
                                        placement='right'
                                      >
                                        <ErrorOutlineIcon color='error' />
                                      </Tooltip>
                                    </Grid>
                                  )}
                                </Grid>
                              </TableCell>
                              <TableCell align='center' sx={{ ...border(item) }}>
                                {rxPrefix ? (
                                  <FacilityAvatar
                                    rxPrefix={rxPrefix}
                                    facilityName={facilityName}
                                    facilityState={facilityState}
                                  />
                                ) : (
                                  <Grid />
                                )}
                              </TableCell>
                              <TableCell sx={{ ...border(item) }} align='center'>
                                {displayStrength}
                              </TableCell>
                              <TableCell sx={{ ...border(item) }}>{item.form}</TableCell>
                              <TableCell align='center' sx={{ ...border(item) }}>
                                {item.prescribedQuantity}
                              </TableCell>
                              <TableCell align='center' sx={{ ...border(item) }}>
                                {item.quantity}
                                {item.isMarkedForFillLater && (
                                  <FillLaterPendingIcon
                                    lineItemId={item.lineItemId}
                                    scope={fillLaterPendingScope({
                                      status: order.status,
                                      isOrderFillLater: order.isFillLater,
                                      processingState: item.processingState,
                                    })}
                                  />
                                )}
                              </TableCell>
                              <TableCell align='center' sx={{ ...border(item) }}>
                                {getPartnerAccountName(order.partnerAccountId)}
                              </TableCell>
                              <TableCell align='center' sx={{ ...border(item) }}>
                                <Tooltip title={item?.documentId ? '' : 'No Docutrack Number'}>
                                  <Typography variant='body2'>
                                    {item?.documentId || 'N/A'}
                                  </Typography>
                                </Tooltip>
                              </TableCell>

                              <TableCell align='center' sx={{ ...border(item) }}>
                                {item.shippingAddress && (
                                  <Tooltip
                                    title={
                                      <Box sx={{ p: 1 }}>
                                        <Typography
                                          variant='body2'
                                          sx={{ fontWeight: 600, mb: 0.5 }}
                                        >
                                          Shipping Address:
                                        </Typography>
                                        <Typography variant='body2'>
                                          {compact([
                                            item.shippingAddress.nameOnAddress,
                                            item.shippingAddress.line1,
                                            item.shippingAddress.line2,
                                            item.shippingAddress.city,
                                            `${item.shippingAddress.state} ${item.shippingAddress.zip}`,
                                          ]).join(', ')}
                                        </Typography>
                                        {item.shippingOption && (
                                          <>
                                            <Typography
                                              variant='body2'
                                              sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}
                                            >
                                              Shipping Option:
                                            </Typography>
                                            <Typography variant='body2'>
                                              {item.shippingOption
                                                .replace(/_/g, ' ')
                                                .toLowerCase()
                                                .replace(/\b\w/g, (char) => char.toUpperCase())}
                                            </Typography>
                                          </>
                                        )}
                                        {order.deliveryInstructions && (
                                          <>
                                            <Typography
                                              variant='body2'
                                              sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}
                                            >
                                              Delivery Instructions:
                                            </Typography>
                                            <Typography
                                              variant='body2'
                                              sx={{ wordBreak: 'break-word' }}
                                            >
                                              {order.deliveryInstructions}
                                            </Typography>
                                          </>
                                        )}
                                      </Box>
                                    }
                                    arrow
                                    placement='top'
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: theme.palette.grey[700],
                                          color: theme.palette.common.white,
                                          fontSize: '0.875rem',
                                          width: 350,
                                          boxShadow: theme.shadows[8],
                                        },
                                      },
                                      arrow: {
                                        sx: {
                                          color: theme.palette.grey[700],
                                        },
                                      },
                                    }}
                                  >
                                    <IconButton size='small'>
                                      <InfoIcon color='primary' fontSize='small' />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>

                              <TableCell align='center' sx={{ ...border(item) }}>
                                {hasAccess.generateBulkLabels &&
                                  !order.isProcessedExternal &&
                                  !isLineItemsInReprocessingState && (
                                    <PrintLabelCheckBox
                                      lineItemId={item.lineItemId}
                                      isDisabled={
                                        item.taggedForReview ||
                                        !item.isLabelCreated ||
                                        order.status === orderEnum.CANCELED ||
                                        order.status === orderEnum.FILL_LATER ||
                                        item.lineItemStatus === lineItemStatusEnum.CANCELED ||
                                        includes(['CANCELED', 'FILL_LATER'], item.processingState)
                                      }
                                      isLabelCreated={item.isLabelCreated}
                                      disabledReason={labelDisabledReason({
                                        status: order.status,
                                        itemStatus: item.lineItemStatus,
                                        processingState: item.processingState,
                                      })}
                                    />
                                  )}
                              </TableCell>
                              <TableCell align='center' sx={{ p: 0, ...border(item) }}>
                                {item.hasPinnedNote && (
                                  <IconButton
                                    onClick={() => setActiveNoteItem(item.lineItemId)}
                                    aria-label={`View pinned notes ${item.lineItemId}`}
                                  >
                                    <StickyNote2Icon color='primary' />
                                  </IconButton>
                                )}
                              </TableCell>

                              <TableCell align='center' sx={{ ...border(item) }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    width: '170px',
                                  }}
                                >
                                  <Chip
                                    sx={{
                                      backgroundColor:
                                        {
                                          CANCELED: theme.palette.error.light,
                                          SHIPPED: theme.palette.success.light,
                                          SPLIT: theme.palette.success.light,
                                        }[item.processingState] || theme.palette.warning.light,
                                      color: theme.palette.primary.contrastText,
                                      height: 35,
                                      ...border(item),
                                      alignSelf: 'flex-start',
                                      alignContent: 'center',
                                    }}
                                    label={
                                      <Typography sx={{ fontSize: '0.8em' }}>
                                        {(() => {
                                          const hasSpecificState = [
                                            'CANCELED',
                                            'SHIPPED',
                                            'IN_SHIPMENT',
                                            'READY_FOR_SHIPMENT',
                                            'SPLIT',
                                            'RESHIPMENT',
                                            'TRANSFERRED',
                                            'FILL_LATER',
                                          ].includes(item.processingState);

                                          if (hasSpecificState) {
                                            return item.processingState.replace(/_/g, ' ');
                                          }

                                          if (order.status === orderEnum.EXPIRED) {
                                            return 'EXPIRED';
                                          }

                                          return 'IN PROGRESS';
                                        })()}
                                      </Typography>
                                    }
                                  />
                                </Box>
                              </TableCell>
                              {item.processingState === 'TRANSFERRED' && item.transferMetadata && (
                                <TableCell align='center' sx={{ ...border(item) }}>
                                  <Button
                                    variant='text'
                                    size='small'
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setTransferDocumentViewerItem(item.lineItemId);
                                    }}
                                    sx={{
                                      fontSize: '0.75rem',
                                      textDecoration: 'underline',
                                      color: theme.palette.primary.main,
                                      minWidth: 'auto',
                                      padding: '4px 8px',
                                    }}
                                  >
                                    View Transfer Document
                                  </Button>
                                </TableCell>
                              )}
                              {item?.processingState === 'SHIPPED' ? (
                                <TableCell align='center' sx={{ ...border(item) }}>
                                  {item?.trackingNumber ? (
                                    <Link
                                      href={
                                        getTrackingInfo({ trackingNumber: item?.trackingNumber })
                                          .trackingUrl
                                      }
                                      target='_blank'
                                      sx={{ fontSize: '1em' }}
                                    >
                                      {item?.trackingNumber}
                                    </Link>
                                  ) : (
                                    <Typography variant='body2'>N/A</Typography>
                                  )}
                                </TableCell>
                              ) : (
                                <TableCell align='right' sx={{ ...border(item) }}>
                                  {!inOrderHistoryTab && item.processingState !== 'SPLIT' && (
                                    <PatientFillProductButton
                                      item={item}
                                      key={item.lineItemId}
                                      mpi={mpi}
                                      orderId={order.orderId}
                                      isProcessedExternal={order.isProcessedExternal}
                                      orderStatus={order.status}
                                      fulfillingPharmacyId={order.fulfillingPharmacyId}
                                      // Refresh both slices after fulfillment so the dispensed NDC
                                      // shows without a reload: orders slice drives the accordion row
                                      // (item.ndc), prescriptions slice drives the Prescription View
                                      // (prescriptionOverride.productDetails).
                                      onSuccess={() => {
                                        dispatch(listPatientOrders({ mpi }));
                                        dispatch(
                                          listPatientPrescriptions({ mpi, forceRefresh: true })
                                        );
                                      }}
                                    />
                                  )}
                                </TableCell>
                              )}
                              <TableCell sx={{ p: 0, ...border(item) }} align='center'>
                                {item.inBatchShipmentQueue &&
                                  !includes(
                                    ['SHIPPED', 'SPLIT', 'CANCELED', 'RESHIPMENT', 'TRANSFERRED'],
                                    item.processingState
                                  ) &&
                                  !order.isProcessedExternal && (
                                    <Tooltip
                                      title={batchShippingTooltip({
                                        isBusinessAddress: item.isBusinessAddress,
                                        isShipped: item.processingState === 'IN_SHIPMENT',
                                        isClickable: hasAccess.viewBatchShipments,
                                      })}
                                      arrow
                                      placement='top'
                                    >
                                      <BatchShippingIcon
                                        onClick={
                                          hasAccess.viewBatchShipments
                                            ? () =>
                                                item.processingState === 'IN_SHIPMENT' &&
                                                item.batchShipmentId
                                                  ? handleOpenBatchShipmentDialog(
                                                      item.batchShipmentId
                                                    )
                                                  : navigateToBatchShipmentQueue(order.orderId)
                                            : undefined
                                        }
                                        color={
                                          item.processingState === 'IN_SHIPMENT'
                                            ? perfectRxTheme.palette.success.main
                                            : perfectRxTheme.palette.warning.warningColour
                                        }
                                        showBadge={item.isBusinessAddress}
                                      />
                                    </Tooltip>
                                  )}
                                {!item.inBatchShipmentQueue &&
                                  item.isBusinessAddress &&
                                  !includes(
                                    ['SHIPPED', 'SPLIT', 'CANCELED', 'RESHIPMENT', 'TRANSFERRED'],
                                    item.processingState
                                  ) &&
                                  !order.isProcessedExternal && <BusinessAddressIndicator />}
                                {item.isFillOverride && (
                                  <WarningAmberIcon
                                    onClick={() => {
                                      setFillOverridesForItem({
                                        lineItemId: item.lineItemId,
                                        overrides: item.fillOverrides,
                                      });
                                    }}
                                    sx={{
                                      color: 'red',
                                      mr: 0.5,
                                      fontSize: 18,
                                      cursor: 'pointer',
                                    }}
                                  />
                                )}

                                {fillOverridesForItem?.lineItemId === item.lineItemId && (
                                  <FillOverridesDialog
                                    open
                                    onClose={() => {
                                      setFillOverridesForItem(null);
                                    }}
                                    overrides={fillOverridesForItem.overrides}
                                    order={order}
                                    lineItem={item}
                                  />
                                )}
                              </TableCell>

                              <TableCell
                                align='center'
                                sx={{
                                  maxWidth: 100,
                                  p: 0,
                                  ...border(item),
                                }}
                              >
                                <PatientNoteOptions
                                  itemId={item.lineItemId}
                                  mpi={mpi}
                                  rxNumber={item.rxNumber}
                                  itemName={item.itemName}
                                  orderId={order.orderId}
                                  itemStatus={item.lineItemStatus}
                                  processingState={item.processingState}
                                  activeNoteItem={activeNoteItem}
                                  setActiveNoteItem={setActiveNoteItem}
                                  addressDirectoryId={item.addressDirectoryId}
                                  inBatchShipmentQueue={item.inBatchShipmentQueue}
                                  isReshipmentOrder={!!order?.reshipmentOf}
                                  addOnQuantity={item.addOnQuantity ?? null}
                                  isMarkedForFillLater={!!item.isMarkedForFillLater}
                                />
                              </TableCell>
                            </TableRow>
                            {!inOrderHistoryTab && (
                              <TableRow>
                                <TableCell colSpan={headCells.length + 6} sx={{ p: 2, pt: 0 }}>
                                  <Grid container justifyContent='center' alignItems='center'>
                                    <Grid item sx={{ mr: 1 }}>
                                      <Chip
                                        sx={{
                                          background: item.taggedForReview
                                            ? perfectRxTheme.palette.warning.warningColour
                                            : theme.palette.success.dark,
                                          color: !item.taggedForReview
                                            ? theme.palette.primary.contrastText
                                            : undefined,
                                          height: 35,
                                        }}
                                        label={
                                          <Typography sx={{ fontSize: '1.1em' }}>
                                            In Review: {item.taggedForReview ? 'Yes' : 'No'}
                                          </Typography>
                                        }
                                      />
                                    </Grid>

                                    <Grid item sx={{ mr: 1 }}>
                                      <Chip
                                        sx={{
                                          background: item.isPrinted
                                            ? theme.palette.success.dark
                                            : perfectRxTheme.palette.warning.warningColour,
                                          color: item.isPrinted
                                            ? theme.palette.primary.contrastText
                                            : undefined,
                                          height: 35,
                                        }}
                                        label={
                                          <Typography sx={{ fontSize: '1.1em' }}>
                                            Printed: {item.isPrinted ? 'Yes' : 'No'}
                                          </Typography>
                                        }
                                      />
                                    </Grid>

                                    <Grid item sx={{ mr: 1 }}>
                                      <Chip
                                        sx={{
                                          background:
                                            item.lineItemStatus === 'PRODUCT_FULFILLED'
                                              ? theme.palette.success.dark
                                              : perfectRxTheme.palette.warning.warningColour,
                                          color:
                                            item.lineItemStatus === 'PRODUCT_FULFILLED'
                                              ? theme.palette.primary.contrastText
                                              : undefined,
                                          height: 35,
                                        }}
                                        label={
                                          <Typography sx={{ fontSize: '1.1em' }}>
                                            Filled:{' '}
                                            {item.lineItemStatus === 'PRODUCT_FULFILLED'
                                              ? 'Yes'
                                              : 'No'}
                                          </Typography>
                                        }
                                      />
                                    </Grid>

                                    {item.addOnQuantity != null && (
                                      <Grid item sx={{ mr: 1 }}>
                                        <Chip
                                          sx={{
                                            background: theme.palette.info.dark,
                                            color: theme.palette.primary.contrastText,
                                            height: 35,
                                          }}
                                          label={
                                            <Typography sx={{ fontSize: '1.1em' }}>
                                              Add On Qty: {item.addOnQuantity}
                                            </Typography>
                                          }
                                        />
                                      </Grid>
                                    )}

                                    {/* POM-6148: one chip per quantity scheduled to fill later */}
                                    {map(
                                      filter(
                                        item.fillLaterItems,
                                        (fillLaterItem) =>
                                          fillLaterItem?.status === FillLaterItemStatus.SCHEDULED
                                      ),
                                      (fillLaterItem) => (
                                        <Grid
                                          item
                                          sx={{ mr: 1 }}
                                          key={
                                            fillLaterItem.fillLaterItemId ??
                                            fillLaterItem.scheduledFor
                                          }
                                        >
                                          <Tooltip
                                            title='Scheduled to fill later. The order is created on this date.'
                                            arrow
                                            placement='top'
                                          >
                                            <Chip
                                              sx={{
                                                background:
                                                  perfectRxTheme.palette.warning.warningColour,
                                                height: 35,
                                              }}
                                              label={
                                                <Typography sx={{ fontSize: '1.1em' }}>
                                                  Fill Later: {fillLaterItem.splitQuantity} on{' '}
                                                  {formatFillLaterDate(fillLaterItem.scheduledFor)}
                                                </Typography>
                                              }
                                            />
                                          </Tooltip>
                                        </Grid>
                                      )
                                    )}

                                    {!isEmpty(item.tags) &&
                                      (() => {
                                        const visibleTags = item.tags.filter(
                                          (tag) => tag?.tagName !== 'BACKFILLED_SHIP_BY'
                                        );
                                        if (isEmpty(visibleTags)) return null;
                                        const sortedTags = sortTags(visibleTags);
                                        return (
                                          <Grid item sx={{ mr: 1 }}>
                                            <Tooltip
                                              title={sortedTags.map(formatTagText).join(', ')}
                                              arrow
                                              placement='top'
                                            >
                                              <Chip
                                                sx={{
                                                  height: 35,
                                                }}
                                                label={
                                                  <Typography sx={{ fontSize: '1.1em' }}>
                                                    Tags:{' '}
                                                    {visibleTags.length === 1 ? (
                                                      formatTagText(first(sortedTags))
                                                    ) : (
                                                      <>
                                                        {formatTagText(first(sortedTags))}{' '}
                                                        {/* eslint-disable-next-line max-len */}
                                                        <strong>
                                                          +{visibleTags.length - 1} more
                                                        </strong>
                                                      </>
                                                    )}
                                                  </Typography>
                                                }
                                              />
                                            </Tooltip>
                                          </Grid>
                                        );
                                      })()}
                                  </Grid>
                                </TableCell>
                              </TableRow>
                            )}
                            {inOrderHistoryTab && (
                              <TableRow>
                                <TableCell
                                  colSpan={headCells.length + 6}
                                  sx={{ borderTop: 'none' }}
                                >
                                  <Grid
                                    container
                                    justifyContent='center'
                                    alignItems='center'
                                    spacing={0.5}
                                    display='flex'
                                  >
                                    {item.processingState === 'RESHIPMENT' &&
                                      !isEmpty(item.reshipmentReason) && (
                                        <>
                                          <Grid item>
                                            <Typography
                                              variant='subtitle2'
                                              sx={{ fontWeight: 600 }}
                                            >
                                              Reshipment Reason(s):
                                            </Typography>
                                          </Grid>
                                          {item.reshipmentReason.map((reason) => (
                                            <Grid item key={reason}>
                                              <Chip
                                                sx={{
                                                  backgroundColor: theme.palette.info.main,
                                                  color: theme.palette.primary.contrastText,
                                                  height: 35,
                                                }}
                                                size='small'
                                                label={
                                                  <Typography sx={{ fontSize: '0.75em' }}>
                                                    {reason.replace(/_/g, ' ')}
                                                  </Typography>
                                                }
                                              />
                                            </Grid>
                                          ))}
                                        </>
                                      )}

                                    {!isEmpty(item.tags) &&
                                      (() => {
                                        const visibleTags = item.tags.filter(
                                          (tag) => tag?.tagName !== 'BACKFILLED_SHIP_BY'
                                        );
                                        if (isEmpty(visibleTags)) return null;
                                        const sortedTags = sortTags(visibleTags);
                                        return (
                                          <Grid item sx={{ mr: 1 }}>
                                            <Tooltip
                                              title={sortedTags.map(formatTagText).join(', ')}
                                              arrow
                                              placement='top'
                                            >
                                              <Chip
                                                sx={{
                                                  height: 35,
                                                }}
                                                label={
                                                  <Typography sx={{ fontSize: '1.1em' }}>
                                                    Tags:{' '}
                                                    {visibleTags.length === 1 ? (
                                                      formatTagText(first(sortedTags))
                                                    ) : (
                                                      <>
                                                        {formatTagText(first(sortedTags))}{' '}
                                                        {/* eslint-disable-next-line max-len */}
                                                        <strong>
                                                          +{visibleTags.length - 1} more
                                                        </strong>
                                                      </>
                                                    )}
                                                  </Typography>
                                                }
                                              />
                                            </Tooltip>
                                          </Grid>
                                        );
                                      })()}
                                  </Grid>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      }
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      {transferDocumentViewerItem && (
        <TransferOutDocumentViewer
          lineItemId={transferDocumentViewerItem}
          open={!!transferDocumentViewerItem}
          onClose={() => setTransferDocumentViewerItem(null)}
        />
      )}
      {viewPrescriptionId && prescriptionsLoaded && (
        <PrescriptionReviewPayloadProvider>
          <PatientPrescriptionDialog
            showRefillOption
            mpi={mpi}
            open={!!viewPrescriptionId}
            contextId={viewPrescriptionId}
            closeDialog={() => setViewPrescriptionId(null)}
            isPrescriptionsTab
          />
        </PrescriptionReviewPayloadProvider>
      )}
      <ShipByDateOverrideDialog
        open={!!shipByDateLineItemId}
        onClose={() => setShipByDateLineItemId(null)}
        showPatientBanner
        lineItem={
          shipByDateLineItemId
            ? {
                ...find(order.lineItems, { lineItemId: shipByDateLineItemId }),
                orderId: order.orderId,
                mpi,
              }
            : null
        }
      />
    </Grid>
  );
};

PatientOrdersAccordion.propTypes = {
  order: PropTypes.shape({
    orderId: PropTypes.string.isRequired,
    referenceId: PropTypes.string.isRequired,
    requestId: PropTypes.string.isRequired,
    status: PropTypes.string,
    source: PropTypes.number,
    orderType: PropTypes.string,
    orderCost: PropTypes.string,
    isProcessedExternal: PropTypes.bool.isRequired,
    fulfillingPharmacyId: PropTypes.string.isRequired,
    partner: PropTypes.shape({
      partnerName: PropTypes.string.isRequired,
      partnerUuid: PropTypes.string.isRequired,
    }),
    practice: PropTypes.shape({ practiceName: PropTypes.string.isRequired }),
    fillType: PropTypes.string,
    reshipmentOf: PropTypes.string,
    fillLaterOf: PropTypes.string,
    isFillLater: PropTypes.bool,
    deliveryInstructions: PropTypes.string,
    shippingAddress: PropTypes.shape({
      line1: PropTypes.string.isRequired,
      line2: PropTypes.string,
      city: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      zip: PropTypes.string.isRequired,
    }).isRequired,
    lineItems: PropTypes.arrayOf(
      PropTypes.shape({
        itemName: PropTypes.string,
        shipByDate: PropTypes.string,
        needsByDate: PropTypes.string,
        itemType: PropTypes.string,
        prescriberName: PropTypes.string,
        lineItemStatus: PropTypes.string.isRequired,
        cost: PropTypes.number,
        lineItemId: PropTypes.string.isRequired,
        prescriptionId: PropTypes.string.isRequired,
        ndc: PropTypes.string,
        isLabelCreated: PropTypes.bool,
        isMarkedForFillLater: PropTypes.bool,
        shippingOption: PropTypes.string,
        verificationStatus: PropTypes.string,
        shippingAddress: PropTypes.shape({
          nameOnAddress: PropTypes.string,
          line1: PropTypes.string,
          line2: PropTypes.string,
          city: PropTypes.string,
          state: PropTypes.string,
          zip: PropTypes.string,
        }),
      })
    ),
    partnerAccountId: PropTypes.string,
  }).isRequired,
  mpi: PropTypes.string.isRequired,
};

export default PatientOrdersAccordion;
